import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Roles } from '@models/roles.models';
import { APP_ROUTES, ITEMS_DIRECT_ROLES, REQUESTS_ROLES } from '@routes';
import { EytcAuthService } from '@services/index';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { LinkColumnComponent } from '../link-column/link-column.component';

@Component({
  selector: 'app-item-id-column',
  standalone: true,
  imports: [LinkColumnComponent],
  templateUrl: './item-id-column.component.html',
  styleUrl: './item-id-column.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemIdColumnComponent implements AgRendererComponent {
  readonly #eytcAuth = inject(EytcAuthService);
  readonly requesterEntityId = signal<string | null>('');
  readonly #roles = this.#eytcAuth.userRoles;

  readonly requestEntityId = signal<string>('');
  readonly requestId = signal<string>('');
  readonly itemEntityId = signal<string>('');
  readonly itemId = signal<string>('');
  readonly newTab = signal<boolean>(false);
  readonly label = signal<string | null>('');
  readonly origin = signal<ItemIdLinkOrigin>(ItemIdLinkOrigin.ItemsGrid);

  readonly routerLink = computed(() => {
    if (this.currentUserIsCustomsBroker() && !this.currentUserIsOwner()) {
      // generate shorter URL
      return ['/app/', 'items', this.itemEntityId(), this.itemId()];
    }

    switch (this.origin()) {
      case ItemIdLinkOrigin.ItemsGrid:
        return [this.itemEntityId(), this.itemId()];
      case ItemIdLinkOrigin.PendingItemsGrid:
        return ['items', this.itemEntityId(), this.itemId()];
      case ItemIdLinkOrigin.DisputeItemsGrid:
        return ['items', this.itemEntityId(), this.itemId()];
      case ItemIdLinkOrigin.ExpediteItemsGrid:
        return ['items', this.itemEntityId(), this.itemId()];
      case ItemIdLinkOrigin.SearchResultsGrid:
        return [
          '/app/',
          APP_ROUTES.REQUESTS,
          APP_ROUTES.CLASSIFICATION,
          this.requestEntityId(),
          this.requestId(),
          this.itemEntityId(),
          this.itemId(),
        ];
      case ItemIdLinkOrigin.AuditHistoryGrid:
        return [
          '/app/',
          APP_ROUTES.REQUESTS,
          APP_ROUTES.CLASSIFICATION,
          this.requestEntityId(),
          this.requestId(),
          this.itemEntityId(),
          this.itemId(),
        ];
      default:
        return [];
    }
  });

  readonly currentUserIsOwner = computed(() => {
    return this.requesterEntityId() === this.#eytcAuth.userProfile()?.id;
  });

  readonly currentUserIsCustomsBroker = computed(() => {
    return this.#roles().includes(Roles.CUSTOMS_BROKER);
  });

  readonly canAccessItemDetails = computed(() => {
    return this.#roles().some(role => REQUESTS_ROLES.includes(role) || ITEMS_DIRECT_ROLES.includes(role));
  });

  agInit(params: ICellRendererParams): void {
    this.origin.set(
      (params as ICellRendererParams & { origin?: ItemIdLinkOrigin }).origin ?? ItemIdLinkOrigin.ItemsGrid
    );

    if (params.colDef?.cellRendererParams?.newTab) {
      this.newTab.set(true);
    }

    this.requestEntityId.set(params.data.requestEntityId);
    this.requestId.set(params.data.requestId);
    this.requesterEntityId.set(params.data.requesterEntityId);
    this.itemEntityId.set(params.data.itemEntityId);
    this.itemId.set(params.data.itemId);
    this.label.set(params.data.itemEntityId ? params.data.itemId : null);
  }

  refresh(params: ICellRendererParams): boolean {
    this.agInit(params);
    return true;
  }
}

export enum ItemIdLinkOrigin {
  ItemsGrid,
  PendingItemsGrid,
  DisputeItemsGrid,
  SearchResultsGrid,
  AuditHistoryGrid,
  ExpediteItemsGrid,
}
